/**
 * The sidebar: where you are and where you can go. Library views, Discover
 * (Flow and friends), your playlists, and anything plugins add -- then, at the
 * bottom, what the engine is doing.
 */

import { EngineControl } from "../EngineControl";
import { useApp } from "../core/app";
import { addToPlaylist, exportPlaylist, newPlaylist } from "../core/commands";
import { useNeedsEdit } from "../core/needsEdit";
import { useRegistry } from "../core/registry";
import { useJobs } from "../core/useJobs";
import { useScanProgress } from "../core/scanProgress";
import { hashOf } from "./Artwork";
import { Icon } from "./Icons";
import { MoreButton } from "./PopupMenu";
import { songs } from "../format/count";
import { toast } from "./Toast";
import { useMay } from "../core/session";

const DOTS = ["var(--accent)", "var(--teal)", "var(--violet)", "var(--rose)", "var(--sky)", "var(--green)"];

export function Sidebar() {
  const app = useApp();
  const { views, ownerNames } = useRegistry();
  const mayDo = useMay();
  useNeedsEdit(); // re-render when the Needs edit count changes (its badge)
  const section = (s: string) => views.filter((v) => v.section === s);

  const item = (id: string, label: string, icon: Parameters<typeof Icon>[0]["name"], badge?: string | null, params?: Record<string, string>) => {
    const on = app.nav.view === id && (!params || Object.entries(params).every(([k, v]) => app.nav.params[k] === v));
    return (
      <button key={id + JSON.stringify(params ?? {})} className={`nv-nav ${on ? "on" : ""}`} aria-current={on ? "page" : undefined} onClick={() => app.navigate(id, params)}>
        <Icon name={icon} />
        <span className="nv-nav-label">{label}</span>
        {badge && <span className="nv-nav-badge">{badge}</span>}
      </button>
    );
  };

  return (
    <aside className="nv-side" aria-label="Sidebar">
      <div className="nv-side-scroll">
        <section>
          <h6>Library</h6>
          {section("library").map((v) => item(v.id, v.label, v.icon, v.badge?.()))}
        </section>
        {section("discover").length > 0 && (
          <section>
            <h6>Discover</h6>
            {section("discover").map((v) => item(v.id, v.label, v.icon, v.badge?.()))}
          </section>
        )}
        <section>
          <h6>
            Playlists
            <button className="nv-iconbtn sm" title="New playlist" disabled={app.offline} onClick={() => void newPlaylist(app)}>
              <Icon name="plus" size={14} />
            </button>
          </h6>
          {app.library.playlists.length === 0 && <div className="nv-side-empty">No playlists yet.</div>}
          {app.library.playlists.map((pl) => {
            const on = app.nav.view === "playlist" && app.nav.params.id === String(pl.id);
            return (
              <div key={pl.id} className={`nv-nav ${on ? "on" : ""}`}>
                <button className="nv-nav-main" onClick={() => app.navigate("playlist", { id: String(pl.id) })} aria-current={on ? "page" : undefined}>
                  <span className="nv-dot" style={{ background: DOTS[hashOf(pl.name) % DOTS.length] }} />
                  <span className="nv-nav-label">{pl.name}</span>
                </button>
                <MoreButton
                  className="sm nv-hover"
                  label={`Options for ${pl.name}`}
                  size={14}
                  items={[
                    { id: "open", label: "Open", icon: "list", run: () => app.navigate("playlist", { id: String(pl.id) }) },
                    { id: "export", group: "b", label: "Export as M3U…", icon: "download", run: () => exportPlaylist(pl.id) },
                    { id: "add", group: "b", label: "Add the selected song", icon: "plus", disabled: !app.focused, run: () => {
                      const t = app.library.tracks.find((x) => x.id === app.focused);
                      if (t) void addToPlaylist(app, pl.id, [t], pl.name);
                    } },
                    { id: "delete", group: "c", label: "Delete playlist", icon: "trash", danger: true, disabled: app.offline, run: async () => {
                      if (!window.confirm(`Delete the playlist “${pl.name}”?\n\nThe songs stay in your library.`)) return;
                      const error = await app.library.playlistAction("delete", { id: pl.id });
                      if (error) return toast(error, "bad");
                      toast(`Deleted “${pl.name}”.`, "ok");
                      if (on) app.navigate("songs");
                    } },
                  ]}
                />
              </div>
            );
          })}
        </section>
        {/* A plugin's screens sit together under the plugin's own name (Radio: Shows,
            Fixed times, Adverts, Play log), not as if each were a plugin of its own. */}
        {[...new Set(section("plugins").filter((v) => v.owner && v.owner !== "core").map((v) => v.owner!))].map((owner) => (
          <section key={owner}>
            <h6>{ownerNames[owner] ?? owner}</h6>
            {section("plugins").filter((v) => v.owner === owner).map((v) => item(v.id, v.label, v.icon, v.badge?.()))}
          </section>
        ))}
        {(mayDo("plugins.manage") || section("plugins").some((v) => !v.owner || v.owner === "core")) && (
          <section>
            <h6>Plugins</h6>
            {section("plugins").filter((v) => !v.owner || v.owner === "core").map((v) => item(v.id, v.label, v.icon, v.badge?.()))}
            {mayDo("plugins.manage") && item("plugins", "Plugin manager", "plug")}
          </section>
        )}
      </div>
      <SideFooter />
    </aside>
  );
}

function SideFooter() {
  const app = useApp();
  const jobs = useJobs(app.offline);
  const r = jobs.report;
  const waiting = r?.tracks.waiting ?? 0;
  const scan = useScanProgress();
  type Row = { kind: string; label: string; done: number; total: number; now: boolean; failed: number };
  const rows: Row[] = [];
  if (scan.listing) rows.push({ kind: "list", label: "Listing files", done: scan.listing.seen, total: scan.listing.total, now: true, failed: 0 });
  else if (scan.reading) rows.push({ kind: "read", label: "Reading new files", done: scan.reading.done, total: scan.reading.total, now: true, failed: 0 });
  for (const s of r?.steps ?? []) {
    if (s.total) rows.push({ kind: s.kind, label: s.label, done: s.done, total: s.total, now: s.running > 0 && !r?.paused, failed: s.failed });
  }
  const busy = rows.length > 0 && (rows[0].kind === "list" || rows[0].kind === "read" || waiting > 0);
  return (
    <footer className="nv-side-foot">
      {app.offline ? (
        <div className="nv-engine-off">
          <div className="nv-engine-line"><span className="nv-led bad" /> Engine not running</div>
          <EngineControl state={app.library.state} onStarted={() => void app.library.refresh()} />
        </div>
      ) : busy && r ? (
        <div>
          <div className="nv-progress-line">
            <span>{r.paused ? "Analysis paused" : "Working through your library"}</span>
          </div>
          <div className="nv-steps">
            {rows.map((s) => {
              const pct = s.total ? Math.round((s.done / s.total) * 100) : 0;
              return (
                <div key={s.kind} className={`nv-step ${s.now ? "now" : ""} ${s.total && s.done >= s.total ? "done" : ""}`}
                  title={`${s.label}: ${s.done.toLocaleString()} of ${s.total.toLocaleString()}${s.now ? " · working on this now" : ""}${s.failed ? ` · ${s.failed} could not be done` : ""}${s.kind === "list" || s.kind === "read" ? " files" : " songs (a song in several files is analysed once)"}`}>
                  <div className="nv-step-line">
                    <span><i className="nv-step-dot" />{s.label}</span>
                    <span>{s.total ? `${s.done.toLocaleString()} / ${s.total.toLocaleString()}` : "…"}</span>
                  </div>
                  <div className="nv-progress thin" role="progressbar" aria-label={s.label} aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
                    <i style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="nv-progress-note">
            {rows.some((x) => x.now) ? "The dot shows what is running" : r.paused ? "Resume to carry on" : "Waiting for work"}
            <button className="nv-link" onClick={() => (r.paused ? jobs.resume() : jobs.pause())}>{r.paused ? "Resume" : "Pause"}</button>
          </div>
        </div>
      ) : (
        <div className="nv-progress-note">
          {songs(app.library.total)}{r?.failed ? ` · ${r.failed} could not be analysed` : " · all analysed"}
        </div>
      )}
    </footer>
  );
}
